import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { API_BASE_URL } from '../utils/apiConfig';
import { useTokenRefresh } from '../hooks/useTokenRefresh';

const MEMBER_ACCESS_TOKEN_KEY = 'member_access_token';
const MEMBER_REFRESH_TOKEN_KEY = 'member_refresh_token';

interface MemberProfile {
  id: string;
  phoneNumber: string;
  fullName?: string | null;
  displayName?: string | null;
  email?: string | null;
  status?: string | null;
  joiningDate?: string | null;
  profilePhoto?: string | null;
  memberProfilePhoto?: string | null;
  gymProfilePhoto?: string | null;
}

interface MemberAuthContextType {
  member: MemberProfile | null;
  token: string | null;
  loading: boolean;
  requestOtp: (phoneNumber: string, recaptchaToken?: string) => Promise<void>;
  verifyOtp: (phoneNumber: string, otp: string, recaptchaToken?: string, deviceId?: string) => Promise<void>;
  refreshMember: () => Promise<void>;
  updateProfilePhoto: (url: string) => void;
  signOut: () => Promise<void>;
}

const MemberAuthContext = createContext<MemberAuthContextType | undefined>(undefined);

export const useMemberAuth = () => {
  const context = useContext(MemberAuthContext);
  if (!context) {
    throw new Error('useMemberAuth must be used within a MemberAuthProvider');
  }
  return context;
};

export const MemberAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [member, setMember] = useState<MemberProfile | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadProfile = async (authToken: string) => {
    const response = await fetch(`${API_BASE_URL}/app/member/profile`, {
      headers: { Authorization: `Bearer ${authToken}` }
    });

    if (!response.ok) {
      throw new Error('Failed to load member profile');
    }

    const data = await response.json();
    setMember(data.data);
  };

  // Handle token refresh
  const handleTokenRefreshed = useCallback((newAccessToken: string) => {
    console.log('[MemberAuth] Access token refreshed');
    localStorage.setItem(MEMBER_ACCESS_TOKEN_KEY, newAccessToken);
    setAccessToken(newAccessToken);
  }, []);

  const handleRefreshFailed = useCallback(() => {
    console.log('[MemberAuth] Token refresh failed - logging out');
    signOut();
  }, []);

  // Set up automatic token refresh
  useTokenRefresh({
    accessToken,
    refreshToken,
    onTokenRefreshed: handleTokenRefreshed,
    onRefreshFailed: handleRefreshFailed
  });

  // Load tokens and profile on mount
  useEffect(() => {
    const savedAccessToken = localStorage.getItem(MEMBER_ACCESS_TOKEN_KEY);
    const savedRefreshToken = localStorage.getItem(MEMBER_REFRESH_TOKEN_KEY);

    if (!savedAccessToken || !savedRefreshToken) {
      setLoading(false);
      return;
    }

    setAccessToken(savedAccessToken);
    setRefreshToken(savedRefreshToken);

    loadProfile(savedAccessToken)
      .catch(() => {
        // If access token expired, try to refresh
        console.log('[MemberAuth] Access token expired, attempting refresh...');
        fetch(`${API_BASE_URL}/api/auth/member/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: savedRefreshToken })
        })
          .then(res => res.json())
          .then(data => {
            if (data.success && data.data.accessToken) {
              const newAccessToken = data.data.accessToken;
              localStorage.setItem(MEMBER_ACCESS_TOKEN_KEY, newAccessToken);
              setAccessToken(newAccessToken);
              return loadProfile(newAccessToken);
            } else {
              throw new Error('Refresh failed');
            }
          })
          .catch(() => {
            // Both tokens invalid, clear everything
            localStorage.removeItem(MEMBER_ACCESS_TOKEN_KEY);
            localStorage.removeItem(MEMBER_REFRESH_TOKEN_KEY);
            setAccessToken(null);
            setRefreshToken(null);
            setMember(null);
          });
      })
      .finally(() => setLoading(false));
  }, []);

  const requestOtp = async (phoneNumber: string, recaptchaToken?: string) => {
    const payload: any = { phoneNumber };
    if (recaptchaToken) {
      payload.recaptchaToken = recaptchaToken;
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/member/check-or-create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const result = await response.json();

    if (!response.ok) {
      // Preserve error response for rate limiting
      const error: any = new Error(result.error || 'Failed to send OTP');
      error.response = { status: response.status, data: result };
      throw error;
    }

    if (!result.success) {
      throw new Error(result.error || 'Failed to send OTP');
    }
  };

  const verifyOtp = async (
    phoneNumber: string,
    otp: string,
    recaptchaToken?: string,
    deviceId?: string
  ) => {
    const payload: any = { phoneNumber, otp };
    if (recaptchaToken) {
      payload.recaptchaToken = recaptchaToken;
    }
    if (deviceId) {
      payload.deviceId = deviceId;
    }

    const response = await fetch(`${API_BASE_URL}/api/auth/member/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const result = await response.json();

    if (!response.ok) {
      // Preserve error response
      const error: any = new Error(result.error || 'Invalid OTP');
      error.response = { status: response.status, data: result };
      throw error;
    }

    if (!result.success) {
      throw new Error(result.error || 'Invalid OTP');
    }

    // PHASE 2: Handle both access and refresh tokens
    const newAccessToken = result.data?.accessToken;
    const newRefreshToken = result.data?.refreshToken;

    if (!newAccessToken || !newRefreshToken) {
      throw new Error('Tokens missing from response');
    }

    // Store both tokens
    localStorage.setItem(MEMBER_ACCESS_TOKEN_KEY, newAccessToken);
    localStorage.setItem(MEMBER_REFRESH_TOKEN_KEY, newRefreshToken);
    setAccessToken(newAccessToken);
    setRefreshToken(newRefreshToken);

    await loadProfile(newAccessToken);
  };

  const refreshMember = async () => {
    if (!accessToken) return;
    await loadProfile(accessToken);
  };

  // Instantly update profilePhoto in context using the URL returned by an upload response,
  // so the UI reflects immediately without an extra fetch round-trip.
  const updateProfilePhoto = (url: string) => {
    setMember(prev => prev ? { ...prev, profilePhoto: url } : prev);
  };

  const signOut = async () => {
    // Revoke refresh token on server
    if (refreshToken) {
      try {
        await fetch(`${API_BASE_URL}/api/auth/member/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken })
        });
      } catch (error) {
        console.error('[MemberAuth] Failed to revoke refresh token:', error);
        // Continue with logout anyway
      }
    }

    // Clear local state and storage
    localStorage.removeItem(MEMBER_ACCESS_TOKEN_KEY);
    localStorage.removeItem(MEMBER_REFRESH_TOKEN_KEY);
    setAccessToken(null);
    setRefreshToken(null);
    setMember(null);
  };

  const value = useMemo(
    () => ({
      member,
      token: accessToken, // Export access token as 'token' for backward compatibility
      loading,
      requestOtp,
      verifyOtp,
      refreshMember,
      updateProfilePhoto,
      signOut
    }),
    [member, accessToken, loading]
  );

  return <MemberAuthContext.Provider value={value}>{children}</MemberAuthContext.Provider>;
};
